import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import requests from "../api";

// Thunk asincrono per ottenere tutti gli articoli
export const fetchAllArticles = createAsyncThunk(
  "articles/fetchAllArticles",
  async (
    { page = 1, limit = 10, article_code = null },
    { rejectWithValue }
  ) => {
    try {
      const requestConfig = requests.getAllArticles(page, limit, article_code);
      const response = await axios(requestConfig);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Errore nel caricamento degli articoli"
      );
    }
  }
);

// Thunk asincrono per attivare/disattivare un articolo
export const activateArticle = createAsyncThunk(
  "articles/activateArticle",
  async (article_code, { rejectWithValue }) => {
    try {
      const requestConfig = requests.activateArticle(article_code);
      const response = await axios(requestConfig);
      return { ...response.data, article_code };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Errore nell'attivazione dell'articolo"
      );
    }
  }
);

// Thunk asincrono per operazioni di massa - attivazione
export const bulkActivateArticles = createAsyncThunk(
  "articles/bulkActivateArticles",
  async (article_codes, { rejectWithValue }) => {
    try {
      const requestConfig = requests.bulkActivateArticles(article_codes);
      const response = await axios(requestConfig);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Errore nell'attivazione di massa degli articoli"
      );
    }
  }
);

// Thunk asincrono per operazioni di massa - disattivazione
export const bulkDeactivateArticles = createAsyncThunk(
  "articles/bulkDeactivateArticles",
  async (article_codes, { rejectWithValue }) => {
    try {
      const requestConfig = requests.bulkDeactivateArticles(article_codes);
      const response = await axios(requestConfig);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Errore nella disattivazione di massa degli articoli"
      );
    }
  }
);

const articlesSlice = createSlice({
  name: "articles",
  initialState: {
    articles: [],
    loading: false,
    error: null,
    activating: {}, // { article_code: boolean } per tracciare lo stato di attivazione
    activationError: null,
    // Stato per operazioni di massa
    bulkOperations: {
      loading: false,
      error: null,
      results: null,
    },
    pagination: {
      currentPage: 1,
      totalPages: 0,
      totalItems: 0,
      itemsPerPage: 10,
      hasNextPage: false,
      hasPrevPage: false,
    },
    filters: {
      article_code: null,
    },
    stats: {
      total: 0,
      active: 0,
      inactive: 0,
      multiPrice: 0,
    },
    statistics: {
      totalActiveArticles: 0,
      totalMultiPriceArticles: 0,
    },
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearActivationError: (state) => {
      state.activationError = null;
    },
    clearBulkOperationError: (state) => {
      state.bulkOperations.error = null;
    },
    clearBulkOperationResults: (state) => {
      state.bulkOperations.results = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        article_code: null,
      };
    },
    updateStats: (state) => {
      // Usa il totale dalla paginazione
      state.stats.total = state.pagination.totalItems || state.articles.length;

      // Usa le statistiche dall'API se disponibili, altrimenti calcola localmente
      if (state.statistics.totalActiveArticles > 0) {
        state.stats.active = state.statistics.totalActiveArticles;
        state.stats.multiPrice = state.statistics.totalMultiPriceArticles;
        // Calcola gli inattivi dal totale
        state.stats.inactive = state.stats.total - state.stats.active;
      } else {
        // Fallback: calcola dalle statistiche locali
        const articles = state.articles;
        state.stats.active = articles.filter(
          (article) => article.visible
        ).length;
        state.stats.inactive = articles.filter(
          (article) => !article.visible
        ).length;
        state.stats.multiPrice = articles.filter(
          (article) => article.price && article.price.length > 1
        ).length;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all articles
      .addCase(fetchAllArticles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllArticles.fulfilled, (state, action) => {
        state.loading = false;
        state.articles = action.payload.data || [];
        state.pagination = action.payload.pagination || state.pagination;
        state.filters = action.payload.filters || state.filters;
        state.statistics = action.payload.statistics || state.statistics;

        // Aggiorna le statistiche
        articlesSlice.caseReducers.updateStats(state);
      })
      .addCase(fetchAllArticles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.articles = [];
      })
      // Activate article
      .addCase(activateArticle.pending, (state, action) => {
        const article_code = action.meta.arg;
        state.activating[article_code] = true;
        state.activationError = null;
      })
      .addCase(activateArticle.fulfilled, (state, action) => {
        const { article_code, new_visible } = action.payload;
        state.activating[article_code] = false;

        // Aggiorna l'articolo nella lista locale
        const articleIndex = state.articles.findIndex(
          (article) => article.article_code === article_code
        );
        if (articleIndex !== -1) {
          state.articles[articleIndex].visible = new_visible;
          state.articles[articleIndex].updated_at = new Date().toISOString();
        }

        // Ricalcola le statistiche
        articlesSlice.caseReducers.updateStats(state);
      })
      .addCase(activateArticle.rejected, (state, action) => {
        const article_code = action.meta.arg;
        state.activating[article_code] = false;
        state.activationError = action.payload;
      })
      // Bulk activate articles
      .addCase(bulkActivateArticles.pending, (state) => {
        state.bulkOperations.loading = true;
        state.bulkOperations.error = null;
        state.bulkOperations.results = null;
      })
      .addCase(bulkActivateArticles.fulfilled, (state, action) => {
        state.bulkOperations.loading = false;
        state.bulkOperations.results = action.payload;

        // Aggiorna gli articoli nella lista locale se disponibili
        if (action.payload.successful_codes) {
          action.payload.successful_codes.forEach((code) => {
            const articleIndex = state.articles.findIndex(
              (article) => article.article_code === code
            );
            if (articleIndex !== -1) {
              state.articles[articleIndex].visible = true;
              state.articles[articleIndex].updated_at =
                new Date().toISOString();
            }
          });
        }

        // Ricalcola le statistiche
        articlesSlice.caseReducers.updateStats(state);
      })
      .addCase(bulkActivateArticles.rejected, (state, action) => {
        state.bulkOperations.loading = false;
        state.bulkOperations.error = action.payload;
      })
      // Bulk deactivate articles
      .addCase(bulkDeactivateArticles.pending, (state) => {
        state.bulkOperations.loading = true;
        state.bulkOperations.error = null;
        state.bulkOperations.results = null;
      })
      .addCase(bulkDeactivateArticles.fulfilled, (state, action) => {
        state.bulkOperations.loading = false;
        state.bulkOperations.results = action.payload;

        // Aggiorna gli articoli nella lista locale se disponibili
        if (action.payload.successful_codes) {
          action.payload.successful_codes.forEach((code) => {
            const articleIndex = state.articles.findIndex(
              (article) => article.article_code === code
            );
            if (articleIndex !== -1) {
              state.articles[articleIndex].visible = false;
              state.articles[articleIndex].updated_at =
                new Date().toISOString();
            }
          });
        }

        // Ricalcola le statistiche
        articlesSlice.caseReducers.updateStats(state);
      })
      .addCase(bulkDeactivateArticles.rejected, (state, action) => {
        state.bulkOperations.loading = false;
        state.bulkOperations.error = action.payload;
      });
  },
});

export const {
  clearError,
  clearActivationError,
  clearBulkOperationError,
  clearBulkOperationResults,
  setFilters,
  clearFilters,
  updateStats,
} = articlesSlice.actions;

export default articlesSlice.reducer;
